/**
 * AdminDataTransferPanel React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import connect from "react-redux/es/connect/connect";

// Material UI
import { withStyles } from '@material-ui/core/styles';

// Material UI components
import {
    ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary,
    Typography, Chip, Button, Checkbox, FormControlLabel
} from '@material-ui/core';

// Material UI icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

// application
import ControlledAccordion from '../base/ControlledAccordion';
import ProgressButton from '../base/ProgressButton';
import FileUploadPanel from "../base/FileUploadPanel";
import {exportDatabase, importDatabase, importUploads, importUserContent} from "../../operations/administration";
import {showError} from "../../operations/message";
import {fetchJSON, showServerCommandMessage} from "../../operations/communication";
import {
    patchServerUrl,
    REST_SERVER_BASE_URL,
    REST_CALL_EXPORT_UPLOADS,
    REST_CALL_EXPORT_USERCONTENT,
    REST_CALL_SYNC_DATABASE,
    REST_CALL_SYNC_UPLOADS
} from "../../config";

/**
 * Panel for admin page for backup functionality
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
class AdminDataTransferPanel extends React.Component {

    state = {
        uploadingUploads: false,
        uploadingDatabase: false,
        uploadingUserContent: false,
        waitingForUploadsFile: false,
        uploadsFile: null,
        waitingForUserContentFile: false,
        userContentFile: null,
        synchronizingDatabase: false,
        synchronizingUploads: false,
        includeHistory: false,
        includePasswords: false
    }

    onUploadUploads = (acceptedFiles, rejectedFiles) => {
        if (this.props.canImportDatabase && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            this.setState({uploadingUploads: true});
            this.props.importUploads(file, errorMessage => this.setState({uploadingUploads: false}));
        }
    }

    onUploadDatabase = (acceptedFiles, rejectedFiles) => {
        if (this.props.canImportDatabase && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            this.setState({uploadingDatabase: true});
            this.props.importDatabase(file, errorMessage => this.setState({uploadingDatabase: false}));
        }
    }

    onUploadUserContent = (acceptedFiles, rejectedFiles) => {
        if (this.props.canImportDatabase && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            this.setState({uploadingUserContent: true});
            this.props.importUserContent(file, errorMessage => this.setState({uploadingUserContent: false}));
        }
    }

    render() {

        const { classes } = this.props;

        const warning = "Je nach Datenmenge und Geschwindigkeit der Anbindung, können folgende Operationen mehrere Minuten oder Stunden dauern. Währenddessen ist der Betrieb des Systems unter Umständen blockiert.";

        return <ControlledAccordion className={classes.accordion} defaultExpandedPanelKey="export">

            <ExpansionPanel key="export">

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Datenexport</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <div className={classes.itemPanel}>
                        <Typography>{warning}</Typography>
                        <div>
                            <Chip label="1" color="primary" className={classes.listNumber} />
                            <div className={classes.buttonPanel}>
                                <FormControlLabel
                                    label="mit Historie"
                                    control={<Checkbox
                                        checked={this.state.includeHistory}
                                        onChange={event => this.setState({includeHistory: event.target.checked})}
                                        color="primary"
                                    />}
                                />
                                <FormControlLabel
                                    label="mit Passwort-Hashes"
                                    control={<Checkbox
                                        checked={this.state.includePasswords}
                                        onChange={event => this.setState({includePasswords: event.target.checked})}
                                        color="primary"
                                    />}
                                />
                                <Button
                                    variant="outlined"
                                    disabled={!this.props.canExportDatabase}
                                    onClick={() => this.props.exportDatabase(this.state.includeHistory, this.state.includePasswords)}
                                    fullWidth={true}
                                >
                                    Datenbank exportieren
                                </Button>
                            </div>
                        </div>
                        <div>
                            <Chip label="2" color="primary" className={classes.listNumber} />
                            {this.renderExportUploadsButton()}
                        </div>
                        {this.props.canExecuteDeveloperCommands && <div>
                            <Chip label="3" color="primary" className={classes.listNumber} />
                            {this.renderExportUserContentButton()}
                        </div>}
                    </div>
                </ExpansionPanelDetails>

            </ExpansionPanel>

            <ExpansionPanel key="import">

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Datenimport</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <div className={classes.itemPanel}>
                        <Typography>{warning}</Typography>
                        <div>
                            <Chip label="1" color="primary" className={classes.listNumber} />
                            <FileUploadPanel
                                onDrop={this.onUploadUploads}
                                disabled={!this.props.canImportDatabase}
                                busy={this.state.uploadingUploads}
                            >
                                Dateiarchiv (zip)<br/>hier ablegen<br/><br/>oder klicken um<br/>Dateiarchiv auszuwählen
                            </FileUploadPanel>
                        </div>
                        <div>
                            <Chip label="2" color="primary" className={classes.listNumber} />
                            <FileUploadPanel
                                onDrop={this.onUploadDatabase}
                                disabled={!this.props.canImportDatabase}
                                busy={this.state.uploadingDatabase}
                            >
                                Datenbankdatei (json)<br/>hier ablegen<br/><br/>oder klicken um<br/>Datenbankdatei auszuwählen
                            </FileUploadPanel>
                        </div>
                        {this.props.canExecuteDeveloperCommands && <div>
                            <Chip label="3" color="primary" className={classes.listNumber} />
                            <FileUploadPanel
                                onDrop={this.onUploadUserContent}
                                disabled={!this.props.canImportDatabase}
                                busy={this.state.uploadingUserContent}
                            >
                                falls vorhanden<br/>User-Content-Ordner (zip)<br/>hier ablegen<br/><br/>oder klicken um<br/>User-Content-Ordner<br/>auszuwählen
                            </FileUploadPanel>
                        </div>}
                    </div>
                </ExpansionPanelDetails>

            </ExpansionPanel>

            {this.props.replicationSource && <ExpansionPanel key="transfer">

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Datenabgleich</Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <div className={classes.itemPanel}>
                        <Typography>{warning}</Typography>
                        <div>
                            <Chip label="1" color="primary" className={classes.listNumber} />
                            <ProgressButton
                                disabled={!this.props.canImportDatabase}
                                busy={this.state.synchronizingUploads}
                                onClick={() => this.syncUploads()}
                            >Dateien übertragen</ProgressButton>
                        </div>
                        <div>
                            <Chip label="2" color="primary" className={classes.listNumber} />
                            <ProgressButton
                                disabled={!this.props.canImportDatabase}
                                busy={this.state.synchronizingDatabase}
                                onClick={() => this.syncDatabase()}
                            >Datenbank übertragen</ProgressButton>
                        </div>
                    </div>
                </ExpansionPanelDetails>

            </ExpansionPanel>}

        </ControlledAccordion>;
    }

    renderExportUploadsButton() {

        if (this.state.uploadsFile) {
            return <Button
                href={patchServerUrl(this.state.uploadsFile)}
                color="primary"
                fullWidth={true}
                download
            >Download</Button>
        }
        else {
            return <ProgressButton
                disabled={!this.props.canExportDatabase}
                busy={this.state.waitingForUploadsFile}
                onClick={() => this.requestDownload(
                    REST_CALL_EXPORT_UPLOADS.method,
                    REST_CALL_EXPORT_UPLOADS.path,
                    "uploadsFile",
                    "waitingForUploadsFile",
                )}
            >Dateien exportieren</ProgressButton>;
        }
    }

    renderExportUserContentButton() {

        if (this.state.userContentFile) {
            return <Button
                href={patchServerUrl(this.state.userContentFile)}
                color="primary"
                fullWidth={true}
                download
            >Download</Button>
        }
        else {
            return <ProgressButton
                disabled={!this.props.canExportDatabase}
                busy={this.state.waitingForUserContentFile}
                onClick={() => this.requestDownload(
                    REST_CALL_EXPORT_USERCONTENT.method,
                    REST_CALL_EXPORT_USERCONTENT.path,
                    "userContentFile",
                    "waitingForUserContentFile",
                )}
            >User-Content-Ordner exportieren (falls vorhanden)</ProgressButton>;
        }
    }

    async requestDownload(method, path, stateFileProp, stateWaitingProp) {

        this.setState({
            [stateWaitingProp]: true,
            [stateFileProp]: null
        });
        try {
            const response = await fetchJSON(
                REST_SERVER_BASE_URL + path,
                {
                    method: method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            this.setState({
                [stateWaitingProp]: false,
                [stateFileProp]: response.file
            });
            console.log("got zip file", response.file);
        }
        catch (error) {
            this.setState({
                [stateWaitingProp]: false,
                [stateFileProp]: null
            });
            this.props.showError(error);
        }
    }

    async syncDatabase() {

        this.setState({
            synchronizingDatabase: true
        });
        try {
            const response = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_SYNC_DATABASE.path,
                {
                    method: REST_CALL_SYNC_DATABASE.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            this.setState({
                synchronizingDatabase: false
            });
            console.log("database synchronized successfully", response.file);
            this.props.showServerCommandMessage("Datenbankabgleich", response);
        }
        catch (error) {
            this.setState({synchronizingDatabase: false});
            this.props.showError(error);
        }
    }

    async syncUploads() {

        this.setState({
            synchronizingUploads: true
        });
        try {
            const response = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_SYNC_UPLOADS.path,
                {
                    method: REST_CALL_SYNC_UPLOADS.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            this.setState({
                synchronizingUploads: false
            });
            console.log("files synchronized successfully", response.file);
            this.props.showServerCommandMessage("Dateiabgleich", response);
        }
        catch (error) {
            this.setState({synchronizingUploads: false});
            this.props.showError(error);
        }
    }
}

const styles = theme => ({
    accordion: {
        width: '100%'
    },
    itemPanel: {
        width: '100%',
        '& > div': {
            display: "flex",
            marginTop: "20px"
        }
    },
    buttonPanel: {
        flexGrow: 1
    },
    listNumber: {
        marginRight: "20px"
    },
    progress: {
        display: "block",
        margin: "10px auto"
    },
    buttonprogress: {
        margin: "0px auto"
    }
});

const mapStateToProps = state => {
    const privileges = state.authorization.userPrivileges;
    return {
        accessToken: state.authorization.accessToken,
        replicationSource: state.configuration.active.replicationsource,
        canExportDatabase: privileges.exportDatabase,
        canImportDatabase: privileges.importDatabase,
        canExecuteDeveloperCommands: privileges.executeDeveloperCommands
    };
};

export default connect(mapStateToProps, {
    exportDatabase, importDatabase, importUploads, importUserContent, showError, showServerCommandMessage
})(withStyles(styles)(AdminDataTransferPanel));